import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: BACKEND_URL,
  timeout: 30000, // 30s timeout — OrgLens API can be slow
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const login = async (email, password = null) => {
  const response = await api.post('/api/auth/login', { email, password });
  return response.data;
};

export const setPassword = async (email, password) => {
  const response = await api.post('/api/auth/set-password', { email, password });
  return response.data;
};

export const getMe = async () => {
  const response = await api.get('/api/auth/me');
  return response.data;
};

export const changePassword = async (oldPassword, newPassword) => {
  const response = await api.post('/api/auth/change-password', null, {
    params: { old_password: oldPassword, new_password: newPassword }
  });
  return response.data;
};

// Admin APIs
export const processDriveLink = async (fileUrl, description = null) => {
  const response = await api.post('/api/admin/process-gdrive-link', {
    file_url: fileUrl,
    description
  });
  return response.data;
};

// Audit APIs
export const getAuditLogs = async (filters = {}) => {
  const response = await api.get('/api/audit-logs', { params: filters });
  return response.data;
};

export const getAuditStats = async () => {
  const response = await api.get('/api/audit-stats');
  return response.data;
};

// Existing APIs
export const uploadFile = async (file, onUploadProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/api/uploads', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
  });
  
  return response.data;
};

export const getUpload = async (uploadId) => {
  const response = await api.get(`/api/uploads/${uploadId}`);
  return response.data;
};

export const listUploads = async () => {
  const response = await api.get('/api/uploads');
  return response.data;
};

export const listIssues = async (params = {}) => {
  const response = await api.get('/api/issues', { params });
  return response.data;
};

export const getIssue = async (issueId) => {
  const response = await api.get(`/api/issues/${issueId}`);
  return response.data;
};

export const addSolution = async (issueId, solution) => {
  const response = await api.post(`/api/issues/${issueId}/solutions`, solution);
  return response.data;
};

export const updateIssueStatus = async (issueId, update) => {
  const response = await api.patch(`/api/issues/${issueId}/status`, update);
  return response.data;
};

export const getStats = async (uploadId = null) => {
  const params = uploadId ? { upload_id: uploadId } : {};
  const response = await api.get('/api/stats', { params });
  return response.data;
};

export const reprocessUpload = async (uploadId) => {
  const response = await api.post(`/api/admin/reprocess/${uploadId}`);
  return response.data;
};

export const purgeAllData = async () => {
  const response = await api.delete('/api/admin/purge-all');
  return response.data;
};

export default api;
